import { useState } from 'react'

import { Box, Grid } from '@factorialco/deprecated-design-system'
import {
  BenefitBankAccountDetailsFragment,
  RetrieveLegalEntitiesAndBankAccountsDocument,
} from 'generated/resources/graphql'

import Callout from 'modules/core/components/Callout'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import AddFundsModal from 'modules/banking/components/AddFundsModal'
import { accountNumberExtraFieldLabel } from 'modules/banking/lib/bankAccountNumbersHelpers'
import { SupportedMarket } from 'modules/benefits/lib/supportedMarkets'
import { BenefitsBankAccountCardSkeleton } from 'modules/benefits/pages/BenefitsHomePage/BenefitsBankAccountCard/BenefitsBankAccountCard'

import { Card } from './Card'

interface BenefitsBankAccountCardListProps {
  activeMarkets: SupportedMarket[]
  loading: boolean
}

const BenefitsBankAccountCardList = ({
  activeMarkets,
  loading: loadingCountries,
}: BenefitsBankAccountCardListProps) => {
  const {
    access: { locale },
    company: { id: companyId, currency },
  } = useCurrentSession()

  const [currentBankAccount, setCurrentBankAccount] =
    useState<BenefitBankAccountDetailsFragment | null>(null)
  const [isAddFundsModalOpen, setIsAddFundsModalOpen] = useState(false)

  useUnsafeEffect(() => {
    if (!currentBankAccount) return

    setIsAddFundsModalOpen(true)
  }, [currentBankAccount])

  const {
    data: legalEntitiesAndBankAccountsData,
    error,
    loading: loadingAccounts,
  } = useQuery(RetrieveLegalEntitiesAndBankAccountsDocument, {
    variables: {
      companyId,
      countryIds: activeMarkets,
    },
    skip: loadingCountries,
  })

  const legalEntities = legalEntitiesAndBankAccountsData?.apiCore.company?.legalEntities || []

  const legalEntitiesWithAssociations = legalEntities.map((legalEntity) => {
    let bankAccount: BenefitBankAccountDetailsFragment | null = null
    let onboarding = null

    // Check if there's ANY benefits activity (completed OR incomplete onboardings)
    const hasBenefitsActivity =
      legalEntity.benefitsBankingAccounts.length > 0 ||
      legalEntity.incompleteOnboardings.edges.length > 0

    if (hasBenefitsActivity) {
      // Rule 1: If LE has completed benefits onboarding, use that
      if (legalEntity.benefitsBankingAccounts.length > 0) {
        const benefitsAccount = legalEntity.benefitsBankingAccounts[0]
        onboarding = benefitsAccount?.onboarding || null // Always set onboarding for benefits accounts
        bankAccount = benefitsAccount?.bankAccounts?.[0] ?? null
      }
      // Rule 2: If no completed benefits but has incomplete onboarding, prioritize that
      else if (legalEntity.incompleteOnboardings.edges.length > 0) {
        // Show the first incomplete onboarding
        const incompleteOnboarding = legalEntity.incompleteOnboardings.edges[0]?.node
        onboarding = incompleteOnboarding || null
        // bankAccount stays null for incomplete onboardings
      }
    }
    // Rule 3: ONLY if no benefits activity at all, check expenses accounts with salary cards
    else if (legalEntity.expensesBankingAccounts.length > 0) {
      // Find the expenses account that has salary cards (not just the first one)
      const expensesAccountWithSalaryCards = legalEntity.expensesBankingAccounts.find((account) => {
        return account.bankAccounts?.some((expensesBankAccount) =>
          expensesBankAccount.cardholdersConnection?.edges?.some((edge) =>
            edge.node?.cards?.some((card) => card.creationSource === 'salary')
          )
        )
      })

      bankAccount = expensesAccountWithSalaryCards?.bankAccounts?.[0] ?? null
    }

    return {
      ...legalEntity,
      onboarding,
      bankAccount,
    }
  })

  const toggleAddFundsModal = (bankAccount: BenefitBankAccountDetailsFragment) => {
    setCurrentBankAccount(bankAccount)
  }

  const closeAddFundsModal = () => {
    setCurrentBankAccount(null)
    setIsAddFundsModalOpen(false)
  }

  const loading = loadingCountries || loadingAccounts

  return (
    <Box gap="s12" flexDirection="row" flexWrap="wrap" data-testid="benefits-bank-account-cards">
      {loading && <BenefitsBankAccountCardSkeleton />}

      {error ? (
        <Callout
          type="critical"
          compact
          description={i18n.t('benefits.homepage.bank_account.error')}
        />
      ) : null}

      <Grid variant="medium">
        {legalEntitiesWithAssociations.map((legalEntity) => (
          <Card
            key={legalEntity.id}
            legalEntity={legalEntity}
            toggleAddFundsModal={toggleAddFundsModal}
            locale={locale}
            currency={currency || 'EUR'}
          />
        ))}
      </Grid>

      {isAddFundsModalOpen && (
        <AddFundsModal
          beneficiaryName={currentBankAccount?.beneficiaryName ?? '-'}
          accountNumber={currentBankAccount?.iban ?? currentBankAccount?.accountNumber ?? '-'}
          bic={currentBankAccount?.bic ?? currentBankAccount?.sortCode ?? '-'}
          extraFieldLabel={
            currentBankAccount?.accountNumberType
              ? accountNumberExtraFieldLabel(currentBankAccount.accountNumberType) || ''
              : ''
          }
          mode="addFunds"
          onClose={() => closeAddFundsModal()}
        />
      )}
    </Box>
  )
}

export { BenefitsBankAccountCardList }
